import { useState } from 'react';
import { Plus, Copy, Trash2, BookImage } from 'lucide-react';
import { useProjectStore } from '../../../state/projectStore';
import { spreadCount } from '../canvas/scene';
import { Dialog, DialogContent, DialogFooter } from '../../ui/Dialog';
import { Button } from '../../ui/Button';
import { t } from '../../../i18n';
import type { PageDocument } from '@fotoboek/domain';
import './page-strip.css';

function MiniPagePreview({ page }: { page?: PageDocument }) {
  if (!page) return <div className="mini-page mini-page-empty" />;
  return (
    <svg viewBox="0 0 200 200" className="mini-page" preserveAspectRatio="none">
      <rect x={0} y={0} width={200} height={200} fill={page.background.color} />
      {[...page.elements]
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((el) => (
          <rect
            key={el.id}
            x={el.x}
            y={el.y}
            width={el.width}
            height={el.height}
            fill={
              el.type === 'photoFrame'
                ? el.assetId
                  ? '#a9cac5'
                  : '#e5e0d6'
                : el.type === 'shape'
                  ? (el.fill ?? '#c8beae')
                  : el.type === 'text'
                    ? '#918a7d'
                    : '#8a8174'
            }
          />
        ))}
    </svg>
  );
}

export function PageStrip() {
  const project = useProjectStore((s) => s.project);
  const view = useProjectStore((s) => s.view);
  const setView = useProjectStore((s) => s.setView);
  const addPage = useProjectStore((s) => s.addPage);
  const removePage = useProjectStore((s) => s.removePage);
  const duplicatePage = useProjectStore((s) => s.duplicatePage);
  const reorderPages = useProjectStore((s) => s.reorderPages);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [pendingRemovePageId, setPendingRemovePageId] = useState<string | null>(null);

  if (!project) return null;
  const total = spreadCount(project);

  return (
    <div className="page-strip">
      <button
        className={view.mode === 'cover' ? 'page-strip-item active' : 'page-strip-item'}
        onClick={() => setView({ mode: 'cover' })}
      >
        <div className="mini-cover">
          <BookImage size={20} />
        </div>
        <span>{t('editor.pageStrip.cover')}</span>
      </button>

      <div className="page-strip-spreads">
        {Array.from({ length: total }, (_, spreadIndex) => {
          const left = project.pages.find((p) => p.order === spreadIndex * 2);
          const right = project.pages.find((p) => p.order === spreadIndex * 2 + 1);
          const isActive = view.mode === 'spread' && view.spreadIndex === spreadIndex;
          return (
            <div
              key={left?.id ?? spreadIndex}
              className={isActive ? 'page-strip-item active' : 'page-strip-item'}
              draggable
              onDragStart={() => setDragIndex(spreadIndex)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIndex !== null && dragIndex !== spreadIndex && left) {
                  reorderPages(dragIndex * 2, spreadIndex * 2);
                }
                setDragIndex(null);
              }}
            >
              <button
                className="page-strip-thumb"
                onClick={() => setView({ mode: 'spread', spreadIndex })}
              >
                <MiniPagePreview page={left} />
                <MiniPagePreview page={right} />
              </button>
              <div className="page-strip-controls">
                <button
                  title={t('editor.pageStrip.duplicatePage')}
                  onClick={() => left && duplicatePage(left.id)}
                >
                  <Copy size={12} />
                </button>
                <button
                  title={t('editor.pageStrip.removePage')}
                  onClick={() => left && setPendingRemovePageId(left.id)}
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <span>{t('editor.pageStrip.spread', { number: spreadIndex + 1 })}</span>
            </div>
          );
        })}
        <button
          className="page-strip-add"
          onClick={() => addPage(project.pages.length - 1)}
          title={t('editor.pageStrip.addPage')}
        >
          <Plus size={18} />
        </button>
      </div>

      <Dialog
        open={!!pendingRemovePageId}
        onOpenChange={(open) => !open && setPendingRemovePageId(null)}
      >
        <DialogContent title={t('editor.pageStrip.removeConfirmTitle')}>
          <p>{t('editor.pageStrip.removeConfirmBody')}</p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPendingRemovePageId(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (pendingRemovePageId) removePage(pendingRemovePageId);
                setPendingRemovePageId(null);
              }}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
